import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, FileText, Contact, Users2, Loader2, X } from "lucide-react";

const GROUPS = [
  { key: "pratiche", label: "Pratiche", icon: FileText },
  { key: "anagrafiche", label: "Anagrafiche", icon: Contact },
  { key: "lead", label: "Lead", icon: Users2 },
];

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q: q.trim() } })
        .then((r) => { setRes(r.data); setOpen(true); })
        .catch(() => setRes(null))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = useCallback((to) => { setOpen(false); setQ(""); setRes(null); navigate(to); }, [navigate]);

  const linkFor = (group, item) =>
    group === "pratiche" ? `/pratiche/${item.id}`
    : group === "anagrafiche" ? `/anagrafiche?open=${item.id}`
    : `/marketing/rubrica/lead/${item.id}`;

  const total = res ? GROUPS.reduce((n, g) => n + (res[g.key]?.length || 0), 0) : 0;

  return (
    <div ref={boxRef} className="relative w-full" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (res) setOpen(true); }}
          placeholder="Cerca pratiche, clienti, lead…"
          className="w-full h-9 rounded-full border border-border bg-muted/40 pl-9 pr-9 text-sm outline-none focus:ring-2 focus:ring-primary focus:bg-background transition-colors"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />}
        {!loading && q && (
          <button data-testid="global-search-clear" onClick={() => { setQ(""); setRes(null); setOpen(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && res && (
        <div data-testid="global-search-results" className="fixed left-2 right-2 top-16 sm:absolute sm:left-0 sm:right-0 sm:top-auto mt-0 sm:mt-2 max-h-[75vh] sm:max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-popover shadow-lg z-50 py-2">
          {total === 0 && <p className="px-4 py-6 text-center text-sm text-muted-foreground">Nessun risultato per “{q}”</p>}
          {GROUPS.map((g) => {
            const items = res[g.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={g.key} className="mb-1 last:mb-0">
                <div className="flex items-center gap-2 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <g.icon className="h-3.5 w-3.5" /> {g.label}
                </div>
                {items.map((it) => (
                  <button
                    key={`${g.key}-${it.id}`}
                    data-testid={`gs-result-${g.key}-${it.id}`}
                    onClick={() => go(linkFor(g.key, it))}
                    className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-accent transition-colors"
                  >
                    <span className="min-w-0">
                      <span className="block break-words sm:truncate text-sm font-medium">{it.numero ? `#${it.numero} · ` : ""}{it.label || "—"}</span>
                      {it.sub && <span className="block truncate text-xs text-muted-foreground">{it.sub}</span>}
                    </span>
                    {it.istituto && <span className="shrink-0 self-start sm:self-center text-[11px] text-muted-foreground">Ist. {it.istituto}</span>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
